import React, {useCallback, useMemo, useState} from 'react';
import Button from "../../../../../../components/base/button/button";
import {deepEqual} from "../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import PartnerDataGrid, {DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../../../containers/data-grid";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {IconButton, TextField, Typography} from "@mui/material";
import {DeleteOutlined} from "@mui/icons-material";
import classNames from "classnames";
import SaveOrRollback from "../../../../../../components/app-specific/save-or-rollback";
import {Alert} from "@mui/lab";
import SalesTaxAutocomplete from "../../../../../../components/text-fields/autocomplete/sales-taxes-autocomplete";

const sanitizeSalesTaxes = (salesTaxes) => {
    if (!salesTaxes?.length) {
        return [];
    }
    return salesTaxes.map(e => ({
        ...e,
        code: Number(e.code ?? 0),
    })).filter(e => !Number.isNaN(e.code) && e.code !== 0);
}

const AddressCardTaxInfoSectionV2 = ({salesTaxes, edit, setShowTaxInfo, readonly = true}) => {
    const operatorAccessLevels = useAccessLevels();
    const sanitizedSalesTaxes = useMemo(() => sanitizeSalesTaxes(salesTaxes), [salesTaxes]);

    const [taxes, setTaxes] = useState(sanitizedSalesTaxes);
    const [editing, setEditing] = useState(false);
    const isMounted = useIsMounted();

    const canAdd = taxes.length < 4;
    const canRemove = taxes.length > 0;
    const canEdit = operatorAccessLevels.packages.sales.manageCustomerAddresses && !readonly;

    /**
     * Updates the local taxes state whenever the salesTaxes prop changes. This ensures that the component reflects the most up-to-date tax information passed down from the parent component.
     */
    React.useEffect(() => {
        setTaxes(sanitizedSalesTaxes);
    }, [sanitizedSalesTaxes])

    const dataGridSaveConfig = React.useMemo(() => ({
        name: canEdit ? "AddressCardV2TaxInfoEditableDataGrid" : 'AddressCardV2TaxInfoDataGrid',
        version: 1,
    }), [canEdit])

    /**
     * Handles changes to the tax code input field. Updates the corresponding tax code in the taxes state array.
     * @type {(function(*, *): void)|*}
     */
    const handleCodeChange = useCallback((index, val) => {
        setTaxes(taxes => taxes.map((tax, i) => i === index ? {...tax, code: val} : tax));
    }, [])

    /**
     * Handles changes to the exempt number input field. Updates the corresponding exempt number in the taxes state array.
     * @param index
     * @param val
     */
    const handleExemptChange = useCallback((index, val) => {
        setTaxes(taxes => taxes.map((tax, i) => i === index ? {...tax, exempt: val} : tax));
    }, [])

    /**
     * Adds a new tax row to the taxes state array with empty code and exempt values.
     * This allows users to input new tax information when editing is enabled.
     * @type {(function(): void)|*}
     */
    const addRow = useCallback(() => {
        setTaxes(prevState => [...prevState, {code: '', exempt: ''}]);
    }, [])

    /**
     * Removes a tax row at the specified index from the taxes state array.
     * @param index
     */
    const removeRow = useCallback((index) => {
        setTaxes(prevState => prevState.filter((_, i) => i !== index));
    }, [])

    /**
     * Saves the changes made to the tax information. Filters out any invalid rows (where tax code is empty or null) before calling the edit function.
     * @returns {Promise<void>}
     */
    const onSave = useCallback(async () => {
        setEditing(true);

        const validTaxes = taxes
            .filter(t => {
                const numberCode = Number(t.code);
                if (Number.isNaN(numberCode)) {
                    return false;
                }
                return numberCode !== 0;
            })
            .map(e => ({...e, code: Number(e.code)}));

        const result = await edit(validTaxes);
        if (!isMounted()) return;
        setEditing(false);
        if (result) {
            setShowTaxInfo(false);
        }
    }, [edit, taxes, setShowTaxInfo, isMounted])

    const hasChanges = React.useMemo(() => !deepEqual(sanitizedSalesTaxes, sanitizeSalesTaxes(taxes)), [sanitizedSalesTaxes, taxes])

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Tax Code",
                name: "taxCode",
                alignment: DataGridColumnAlignments.left,
                type: canEdit ? DataGridColumnTypes.element : DataGridColumnTypes.string,
                sortable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    value: 1,
                }
            },
            {
                title: "Exempt No",
                name: "exemptNo",
                alignment: DataGridColumnAlignments.left,
                type: canEdit ? DataGridColumnTypes.element : DataGridColumnTypes.string,
                sortable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    value: 1,
                }
            },
            ...(canEdit && canRemove ? [{
                name: 'delete',
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 0,
                resizable: false,
                sortable: false,
            }] : []),
        ], [canEdit, canRemove])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return taxes?.map((item, index) => ({
            key: item.id,
            data: item,
            cells: {
                taxCode: !canEdit ? item.code
                    : <SalesTaxAutocomplete
                        freeSolo
                        value={item.code?.toString()}
                        onChange={(e, value) => handleCodeChange(index, typeof value === 'string' ? value : value?.code)}
                        disabled={editing}
                        placeholder="Code"
                        size={'small'}
                        type="number"
                        fullWidth
                    />,
                exemptNo: !canEdit ? item.exempt
                    : <TextField
                        value={item.exempt ?? ''}
                        onChange={e => handleExemptChange(index, e.target.value)}
                        disabled={editing}
                        placeholder="Exempt Number"
                        size={'small'}
                        fullWidth
                    />,
                delete: !canEdit || !canRemove ? null : (
                    <IconButton
                        onClick={() => removeRow(index)}
                        disabled={editing}
                        color={'paper.error'}
                    >
                        <DeleteOutlined fontSize={'small'}/>
                    </IconButton>
                )
            }
        }))
    }, [taxes, canEdit, editing, canRemove, handleCodeChange, handleExemptChange, removeRow])

    return (
        <div className={'address-card-tax-info-content px-3 pb-3'}>
            {
                !taxes.length &&
                <div className={'w-100'}>
                    <p className={'d-flex align-items-center justify-content-center text-center text-sm my-4'}>
                        This address does not have any tax information. {canEdit && "Click the button below to add tax information for this address."}
                    </p>
                </div>
            }
            {
                !!taxes.length &&
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{columns: columns,}}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    state={{pagination: {currentPage: 1, pageSize: taxes.length}, density: dataGridDensity,}}
                    hideFooter
                    hideToolbar
                    visibleRows={taxes.length}
                    classNames={{
                        table: 'mt-0',
                        layout: 'mt-2',
                    }}
                />
            }
            {
                canEdit && canAdd &&
                <div className={classNames("w-100 mb-3", {'mt-0 d-flex align-items-center justify-content-center': !taxes.length})}>
                    <Button variant={!taxes.length ? 'outlined' : "text"} color="primary" onClick={addRow} disabled={editing}>
                        + Add Tax Row
                    </Button>
                </div>
            }
            {
                canEdit && hasChanges &&
                <Alert severity={'info'}>
                    <Typography variant={'body2'} color={'text.primary'} className={'me-3'}>
                        You have unsaved changes. be sure to save them before exiting.
                    </Typography>
                    <SaveOrRollback
                        column={false}
                        show={hasChanges}
                        loading={editing}
                        onRollback={() => setTaxes(sanitizedSalesTaxes)}
                        onSave={() => onSave()}
                    />
                </Alert>
            }
        </div>
    );
}

export default AddressCardTaxInfoSectionV2;
